import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useI18n } from '../../i18n/I18nContext';
import { FaCheckCircle, FaExclamationTriangle, FaShieldAlt, FaCamera, FaFileSignature } from 'react-icons/fa';
import api from '../../services/api';
import './MissionSettings.css';

const MissionSettings = () => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [settings, setSettings] = useState({
    enforceVehicleChecklist: false,
    enforcePodPhoto: false,
  });

  const [initialSettings, setInitialSettings] = useState({});

  useEffect(() => {
    fetchProfileSettings();
  }, []);

  const fetchProfileSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/users/me');
      if (res.data) {
        const fetchedSettings = {
          enforceVehicleChecklist: res.data.enforceVehicleChecklist === true,
          enforcePodPhoto: res.data.enforcePodPhoto === true,
        };
        setSettings(fetchedSettings);
        setInitialSettings(fetchedSettings);
      }
    } catch (err) {
      console.error(err);
      setError(t('missions.settings.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (setting) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // We need to fetch the current user profile first to just update these fields
      const currentUser = await api.get('/users/me');
      
      const updatePayload = {
        firstname: currentUser.data.firstname,
        lastname: currentUser.data.lastname,
        country: currentUser.data.country,
        enforceVehicleChecklist: String(settings.enforceVehicleChecklist),
        enforcePodPhoto: String(settings.enforcePodPhoto),
      };

      await api.put('/users/me', updatePayload);
      
      setInitialSettings(settings);
      setSuccess(t('missions.settings.success'));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error(err);
      setError(t('missions.settings.error'));
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(initialSettings);

  return (
    <DashboardLayout>
      <div className="mission-settings-wrapper p-4">
        
        {/* Header */}
        <div className="settings-header mb-4">
          <div className="d-flex align-items-center mb-2">
            <FaShieldAlt className="header-icon me-3 text-primary" size={28} />
            <h1 className="h3 mb-0 fw-bold">{t('missions.settings.title')}</h1>
          </div>
          <p className="text-muted ms-5">{t('missions.settings.subtitle')}</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-danger d-flex align-items-center ms-5">
            <FaExclamationTriangle className="me-2" />
            <div>{error}</div>
          </div>
        )}

        {success && (
          <div className="alert alert-success d-flex align-items-center ms-5 slide-in-top">
            <FaCheckCircle className="me-2" />
            <div>{success}</div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="d-flex justify-content-center my-5 ms-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        ) : (
          <div className="settings-content ms-5 max-w-lg">
            
            <div className="settings-card mb-4 shadow-sm border-0 bg-white rounded-4 overflow-hidden">
              <div className="card-body p-4">
                
                {/* Setting 1: Vehicle Checklist */}
                <div className="setting-item d-flex align-items-start p-3 mb-3 rounded-3 setting-hover transition-all">
                  <div className="setting-icon-wrapper bg-light-primary text-primary me-3 rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                    <FaCamera size={20} />
                  </div>
                  <div className="setting-text flex-grow-1">
                    <h5 className="mb-1 text-dark fw-bold">{t('missions.settings.vehicleChecklist.title')}</h5>
                    <p className="text-muted small mb-0 lh-sm">
                      {t('missions.settings.vehicleChecklist.description')}
                    </p>
                  </div>
                  <div className="setting-toggle ms-4 pt-2">
                    <div className="form-check form-switch form-switch-lg">
                      <input 
                        className="form-check-input cur-pointer" 
                        type="checkbox" 
                        role="switch" 
                        checked={settings.enforceVehicleChecklist}
                        onChange={() => handleToggle('enforceVehicleChecklist')}
                        id="toggle-vehicle"
                      />
                    </div>
                  </div>
                </div>

                <hr className="text-muted opacity-25" />

                {/* Setting 2: POD Checkout */}
                <div className="setting-item d-flex align-items-start p-3 mt-3 rounded-3 setting-hover transition-all">
                  <div className="setting-icon-wrapper bg-light-success text-success me-3 rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                    <FaFileSignature size={20} />
                  </div>
                  <div className="setting-text flex-grow-1">
                    <h5 className="mb-1 text-dark fw-bold">{t('missions.settings.podChecklist.title')}</h5>
                    <p className="text-muted small mb-0 lh-sm">
                      {t('missions.settings.podChecklist.description')}
                    </p>
                  </div>
                  <div className="setting-toggle ms-4 pt-2">
                    <div className="form-check form-switch form-switch-lg">
                      <input 
                        className="form-check-input cur-pointer" 
                        type="checkbox" 
                        role="switch" 
                        checked={settings.enforcePodPhoto}
                        onChange={() => handleToggle('enforcePodPhoto')}
                        id="toggle-pod"
                      />
                    </div>
                  </div>
                </div>

              </div>
              <div className="card-footer bg-light border-0 px-4 py-3 d-flex justify-content-end align-items-center">
                 <button 
                  className="btn btn-primary px-4 py-2 rounded-pill shadow-sm fw-medium transition-transform btn-save-animated"
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                >
                  {saving ? (
                    <><span className="spinner-border spinner-border-sm me-2" />{t('missions.settings.saving')}</>
                  ) : (
                    t('missions.settings.save')
                  )}
                </button>
              </div>
            </div>

          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MissionSettings;
